/**
 * FornadaItemDialog Component
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { receitasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import type { FornadaItem } from "@/types/fornada";

interface FornadaItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: FornadaItem | null;
  onSave: (item: FornadaItem) => void;
}

export function FornadaItemDialog({ open, onOpenChange, item, onSave }: FornadaItemDialogProps) {
  const { toast } = useToast();
  const [receitas, setReceitas] = useState<any[]>([]);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [selectedReceita, setSelectedReceita] = useState<any | null>(null);
  const [quantidade, setQuantidade] = useState<number>(1);
  const [observacoes, setObservacoes] = useState("");

  useEffect(() => {
    if (open) {
      loadReceitas();
      if (item) {
        // Modo edição
        setQuantidade(item.quantidade_produzida);
        setObservacoes(item.observacoes_item || "");
        setSelectedReceita({
          id_receita: item.id_receita,
          nome: item.receita_nome,
          rendimento: item.receita_rendimento,
          custo_total: item.receita_custo_total,
        });
      } else {
        // Modo criação
        setQuantidade(1);
        setObservacoes("");
        setSelectedReceita(null);
      }
    }
  }, [open, item]);

  const loadReceitas = async () => {
    try {
      const result = await receitasAdapter.list({ limit: 1000 });
      setReceitas(result.items);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar receitas",
        description: error.message,
      });
    }
  };

  const handleSave = () => {
    if (!selectedReceita) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Selecione uma receita",
      });
      return;
    }

    if (quantidade <= 0) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "A quantidade deve ser maior que zero",
      });
      return;
    }

    const custoUnitario = selectedReceita.rendimento > 0 
      ? selectedReceita.custo_total / selectedReceita.rendimento 
      : 0;
    const custoTotal = custoUnitario * quantidade;

    const novoItem: FornadaItem = {
      id_fornada_item: item?.id_fornada_item || crypto.randomUUID(),
      id_fornada: item?.id_fornada || "",
      id_receita: selectedReceita.id_receita,
      tenant_id: item?.tenant_id || "",
      receita_nome: selectedReceita.nome,
      receita_rendimento: selectedReceita.rendimento,
      receita_custo_total: selectedReceita.custo_total,
      quantidade_produzida: quantidade,
      custo_unitario_fixado: custoUnitario,
      custo_total_item: custoTotal,
      observacoes_item: observacoes || null,
      dt_cadastro: item?.dt_cadastro || new Date().toISOString(),
      criado_por: item?.criado_por || null,
    };

    onSave(novoItem);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{item ? "Editar Receita" : "Adicionar Receita"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Receita *</Label>
            <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCombobox}
                  className="w-full justify-between"
                  disabled={!!item} // Não permite trocar receita em modo edição
                >
                  {selectedReceita ? selectedReceita.nome : "Selecione uma receita..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Buscar receita..." />
                  <CommandEmpty>Nenhuma receita encontrada</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-y-auto">
                    {receitas.map((receita) => (
                      <CommandItem
                        key={receita.id_receita}
                        value={receita.nome}
                        onSelect={() => {
                          setSelectedReceita(receita);
                          setOpenCombobox(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedReceita?.id_receita === receita.id_receita ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {receita.nome}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {selectedReceita && (
            <div className="bg-muted p-3 rounded-md text-sm space-y-1">
              <p><strong>Rendimento:</strong> {selectedReceita.rendimento || 0} unidades</p>
              <p><strong>Custo Total:</strong> R$ {selectedReceita.custo_total?.toFixed(2) || "0.00"}</p>
              <p><strong>Custo Unitário:</strong> R$ {(selectedReceita.custo_total / (selectedReceita.rendimento || 1)).toFixed(2)}</p>
            </div>
          )}

          <div>
            <Label htmlFor="quantidade">Quantidade Produzida *</Label>
            <Input
              id="quantidade"
              type="number"
              step="0.001"
              value={quantidade}
              onChange={(e) => setQuantidade(Number(e.target.value))}
              placeholder="Ex: 10"
            />
          </div>

          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Observações sobre este item..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            {item ? "Salvar" : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
